import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return setStatus("error");
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) return setStatus("timeout");
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.booking_id) {
            try { const b = await api.get(`/bookings/${data.booking_id}`); setBooking(b.data); } catch {}
          }
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [params]);

  return (
    <section className="max-w-xl mx-auto px-5 sm:px-8 py-28">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-sm bg-navy-light border border-gold/30 p-10 text-center" data-testid="payment-success">
        {status === "checking" && (<><Loader2 className="h-14 w-14 text-gold mx-auto mb-5 animate-spin" /><h1 className="font-heading text-2xl font-bold text-white">Confirming your payment…</h1><p className="text-slate-400 mt-2">Please wait a moment.</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-5" />
          <h1 className="font-heading text-3xl font-bold text-white">Payment successful!</h1>
          <p className="text-slate-300 mt-3">{booking && booking.balance_due > 0.01 ? "Thank you! Your deposit is received and your ride is confirmed." : "Your ride is booked and paid. See you soon!"}</p>
          {booking && (
            <div className="mt-8 text-left rounded-sm bg-navy border border-gold/20 p-6 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Service</span><span className="text-white text-right">{booking.description}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Date & time</span><span className="text-white">{booking.date} at {booking.time}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Total fare</span><span className="text-white">${booking.amount.toFixed(2)} {booking.currency}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Paid so far</span><span className="text-gold font-semibold" data-testid="success-paid">${(booking.amount_paid ?? booking.amount).toFixed(2)} {booking.currency}</span></div>
              {booking.balance_due > 0.01 && <div className="flex justify-between"><span className="text-slate-400">Balance due</span><span className="text-white font-semibold" data-testid="success-balance">${booking.balance_due.toFixed(2)} {booking.currency}</span></div>}
              <div className="flex justify-between"><span className="text-slate-400">Reference</span><span className="text-white">{booking.id.slice(0, 8).toUpperCase()}</span></div>
            </div>
          )}
          {booking && booking.balance_due > 0.01 && (
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate(`/pay/${booking.id}`)} data-testid="success-paybalance-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light">
                Pay the balance by card
              </button>
              <button onClick={() => navigate(`/split/${booking.id}`)} data-testid="success-split-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-6 py-3 font-semibold text-gold transition-colors duration-200 hover:bg-gold/10">
                Split with your group
              </button>
            </div>
          )}
        </>)}
        {(status === "timeout" || status === "error") && (<>
          <XCircle className="h-16 w-16 text-slate-400 mx-auto mb-5" />
          <h1 className="font-heading text-2xl font-bold text-white">We couldn't confirm yet</h1>
          <p className="text-slate-400 mt-2">If you completed payment, your booking is safe — we'll email you a confirmation.</p>
        </>)}
        <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light" data-testid="success-home-btn">
          Back to home <ArrowRight className="h-4 w-4" />
        </button>
      </motion.div>
    </section>
  );
}
